<script setup lang="ts" generic="T extends string">
/**
 * NxSelectInput for one value: `v-model` is the option itself, or `null` for
 * none — instead of the one-element array (`[x]` / `[null]`) the underlying
 * select works with. Everything else (options, `optionText`, `optionGroup`,
 * `allowNull`, the `option` and `placeholder` slots) passes straight through.
 *
 * Options are strings. For numeric ids, pass `String(id)` as the option and
 * the name via `optionText`, and convert at the caller. An empty string means
 * „nothing selected“ (as in the admin adapters it replaces), so `''` is never
 * a valid option — give such an entry a real key.
 */
import type { StyleValue } from 'vue';
import { computed } from 'vue';
import type { InputHeight } from './internal';
import NxSelectInput from './NxSelectInput.vue';

const props = defineProps<{
  id?: string;
  options: T[];
  placeholder: string;
  wrapperStyle?: StyleValue;
  height?: InputHeight;
  disabled?: boolean;
  /** User can pick the placeholder again, which sets the value to `null`. */
  allowNull?: boolean;
  revealIcon?: boolean;
  hideIcon?: boolean;
  fitDropdownWidth?: boolean;
  ariaKeyshortcuts?: string;
  /**
   * Accessible name of the focused trigger. Forwarded to `NxSelectInput`.
   * A fallthrough `aria-label` stays on the dropdown root.
   */
  triggerAriaLabel?: string;
  /** Forwarded onto the trigger. A fallthrough `aria-describedby` stays on the root. */
  triggerAriaDescribedby?: string;
  arrowKeysNavigate?: boolean;
  optionText?: (option: T) => string;
  optionDisabled?: (option: T) => boolean;
  optionGroup?: (option: T) => string | undefined;
}>();

/** The selected option, `null` for none. */
const model = defineModel<T | null>({ required: true });

const arrayModel = computed<T[] | [null]>({
  get: (): T[] | [null] => (model.value == null || model.value === '' ? [null] : [model.value]),
  set: (value: T[] | [null]) => {
    const next = value?.[0];
    model.value = next == null || next === '' ? null : next;
  },
});
</script>

<template>
  <NxSelectInput
    v-model="arrayModel"
    :id="props.id"
    :options="props.options"
    :placeholder="props.placeholder"
    :wrapper-style="props.wrapperStyle"
    :height="props.height"
    :disabled="props.disabled"
    :allow-null="props.allowNull"
    :reveal-icon="props.revealIcon"
    :hide-icon="props.hideIcon"
    :fit-dropdown-width="props.fitDropdownWidth"
    :aria-keyshortcuts="props.ariaKeyshortcuts"
    :trigger-aria-label="props.triggerAriaLabel"
    :trigger-aria-describedby="props.triggerAriaDescribedby"
    :arrow-keys-navigate="props.arrowKeysNavigate"
    :option-text="props.optionText"
    :option-disabled="props.optionDisabled"
    :option-group="props.optionGroup"
  >
    <template v-if="$slots.option" #option="{ option }">
      <slot name="option" :option="option" />
    </template>
    <template v-if="$slots.placeholder" #placeholder="{ placeholder }">
      <slot name="placeholder" :placeholder="placeholder" />
    </template>
  </NxSelectInput>
</template>
